/**
 * Modal store — leaf module. Single source of truth for the app-wide modal
 * (which modal is open, its props/options, and a pending-modal queue used when
 * swapping modals).
 *
 * Moved out of store/index.js (Perf 3.1.b). The only cross-store dependency is
 * a RUNTIME auth lookup for anon-gating (see getAuthStore below) — resolved
 * lazily so this leaf does not statically import the auth slice.
 */
import { create } from 'zustand';
import { Platform, Keyboard } from 'react-native';

// Lazy auth accessor — runtime-only so the modal leaf never statically pulls
// the auth store (and its transitive api/socket graph) into a public chunk.
// Points at the auth leaf directly (Perf 3.1.b Step 3).
const getAuthStore = () => require('./auth').useAuthStore;

// Public-dashboard wrap-up (Phase 7): any modal listed in
// `ANON_GATED_MODAL_NAMES` requires an authenticated user. If `openModal`
// is invoked for one of these while the user is anonymous, we transparently
// swap to `AuthCTAModal` so we never present a modal whose underlying API
// would 401. This is defense-in-depth — individual callsites should still
// route through `useRequireAuth` so we can attach contextual copy. The
// fallback copy below is intentionally generic.
const ANON_GATED_MODAL_NAMES = new Set([
  'SettingsModalWeb',
  'SettingsModalMobile',
  'FileUploadModal',
  'CreateCollectionModal',
  'CollectionMemorySettingsModal',
  'MoveToCollectionModal',
  'MoveChatsToCollectionModal',
  'CollectionsIconGridModal',
  'CustomAgentModal',
  'CreateMemoryModal',
  'CreateTaskModal',
  'WorkChatTasksModal',
  'SetupTaskFromTemplateModal',
  'MusicLyricsModal',
  'PricingModal',
  'CreditsPurchaseModal',
  'OutOfCreditsModal',
  'ModelAccessPaywallModal',
  'CreditsRequiredModal',
  'BookmarkFolderPickerModal',
  'MoveCollectionModal',
  'AddFilesToCollectionModal',
  'FileEditModal',
  'ReportModal',
  'QuickPurchaseConfirmModal',
  'SubscriptionExtendModal',
  'VoiceCreditsDepletedModal',
  'PetPickerModal',
  'VerifyEmailModal',
  'VerifyPhoneModal',
  'AccountReviewHoldModal',
]);

function modalIsAnonGated(modalName) {
  return ANON_GATED_MODAL_NAMES.has(modalName);
}

function isAnonUser() {
  const auth = getAuthStore().getState();
  return !auth?.token || (typeof auth?.isTokenExpired === 'function' && auth.isTokenExpired());
}

const ANON_GATE_PROPS = {
  source: 'modal_store_anon_gate',
  title: 'Sign in to continue',
  subtitle: 'This action is tied to your account \u2014 create one or log in to use it.',
  icon: 'lock',
};


function navigateToSettings(modalName, props) {
  if (modalName === 'CustomAgentModal' && !props.agentId) {
    require('expo-router').router.navigate('/agents/create');
    return true;
  }
  if (modalName !== 'SettingsModalWeb' && modalName !== 'SettingsModalMobile') return false;
  const { router } = require('expo-router');
  const section = props.tab || props.initialScreen || 'appearance';
  router.navigate({ pathname: '/settings', params: { tab: section, page: undefined, detail: undefined } });
  return true;
}

export const useModalStore = create((set, get) => ({
  // State
  isOpen: false,
  modalName: null,
  modalProps: {},
  options: {},
  pendingModal: null,
  // Actions
  actions: {
    openModal: (modalName, props = {}, options = {}) => {
      if (Platform.OS !== 'web') Keyboard.dismiss();
      // Defense-in-depth: anon users never see per-user modals; we
      // re-route to AuthCTA with the original modal's name attached so
      // analytics can attribute downstream signups.
      if (modalName !== 'AuthCTAModal' && modalIsAnonGated(modalName) && isAnonUser()) {
        set({
          isOpen: true,
          modalName: 'AuthCTAModal',
          modalProps: {
            ...ANON_GATE_PROPS,
            source: `${ANON_GATE_PROPS.source}_${modalName}`,
            intendedModal: modalName,
            intendedProps: props,
          },
          options,
          pendingModal: null,
        });
        return;
      }
      // Older entry points now open the persistent settings page.
      if (navigateToSettings(modalName, props)) {
        set({ isOpen: false, pendingModal: null });
        return;
      }
      set({
        isOpen: true,
        modalName,
        modalProps: props,
        options,
        pendingModal: null,
      });
    },
    closeModal: () => {
      set({ isOpen: false });
    },
    clearModal: () => {
      if (get().isOpen) return;
      const pending = get().pendingModal;
      if (pending) {
        if (Platform.OS !== 'web') Keyboard.dismiss();
        set({
          isOpen: true,
          modalName: pending.modalName,
          modalProps: pending.modalProps || {},
          options: pending.options || {},
          pendingModal: null,
        });
        return;
      }
      set({
        isOpen: false,
        modalName: null,
        modalProps: {},
        options: {},
        pendingModal: null,
      });
    },
    closeAndOpenModal: (modalName, props = {}, options = {}) => {
      // Same anon-gate protection as `openModal` so an in-progress
      // modal swap doesn't bypass the auth CTA.
      if (modalName !== 'AuthCTAModal' && modalIsAnonGated(modalName) && isAnonUser()) {
        set({
          isOpen: false,
          pendingModal: {
            modalName: 'AuthCTAModal',
            modalProps: {
              ...ANON_GATE_PROPS,
              source: `${ANON_GATE_PROPS.source}_${modalName}`,
              intendedModal: modalName,
              intendedProps: props,
            },
            options,
          },
        });
        return;
      }
      if (navigateToSettings(modalName, props)) {
        set({ isOpen: false, pendingModal: null });
        return;
      }
      set({
        isOpen: false,
        pendingModal: { modalName, modalProps: props, options },
      });
    },
  },
}));

export default useModalStore;
